import { call, put, select, takeLatest } from 'redux-saga/effects'
import { PatientInformationConstants } from '../store/patientInformation.constants'
import { getPcrIdSelector } from '../../home/selectors'
import { IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { initialPatientAllergiesState } from '../../patientAllergies/store/patientAllergies.reducers'
import {
  initialPatientDemographicsState,
  physicianDetailsInit,
} from '../../patientDemographics/store/patientDemographics.reducer'
import { initialPatientMedicalHistoryState } from '../../patientMedicalHistory/store/patientMedicalHistory.reducers'
import { initialPatientMedicationsState } from '../../patientMedications/store/patientMedications.reducers'
import { refreshPatientData, setHasPatient, setSearchImport } from '../store/patientInformation.actions'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { initialPatientImmunizationsState } from '../../patientImmunizations/store/patientImmunizations.reducers'
import { initialInsuranceInformationState } from '../../insuranceInformation/store/insuranceInformation.reducers'
import { initialRelationshipsAndGuarantorsState } from '../../relationshipsAndGuarantors/store/relationshipsAndGuarantors.reducers'
import { initialCmsInformationState } from '../../cmsInformation/store/cmsInformation.reducers'
import { initialPaymentInformationState } from '../../paymentInformation/store/paymentInformation.reducers'
import { initialPcsInformationState } from '../../pcsInformation/store/pcsInformation.reducers'
import { initialAuthorizationInformationState } from '../../authorizationInformation/store/authorizationInformation.reducers'
import { refreshBillingData } from '../../billing/store/billing.actions'
import { IOption } from '../../../common/common.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getPatientDemographicsOptionsSelector } from '../../options/selectors'
import { IPatientDemographicsOptionsState } from '../../options/store/options.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { getRulesSelector } from '../../rules/selectors'
import { IRulesState } from '../../rules/store/rules.types'
import { clearPatientSearchErrors } from '../../../utilities/runRulesValidation.util'
import { clearPhysicianData } from '../../patientDemographics/store/patientDemographics.actions'
import { resetEmployerState } from '../../patientDemographics/employer/store/employer.actions'

export function* replacePatient() {
  const pcrDocumentId: string = yield select(getPcrIdSelector)
  const serviceId: string = yield select(getServiceIdSelector)
  const serviceConfig: IServiceConfig = yield select(getServiceConfigSelector)
  const pcrDocsService = new PcrDocsService(serviceId)
  const patientDemographicsOptions: IPatientDemographicsOptionsState = yield select(
    getPatientDemographicsOptionsSelector
  )
  const { DEFAULT_WEIGHT_MEASUREMENT, DEFAULT_LENGTH_MEASUREMENT } = serviceConfig
  const { weightUnitOptions, heightUnitOptions } = patientDemographicsOptions
  const rules: IRulesState = yield select(getRulesSelector)

  try {
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrDocumentId)
    const defaultWeightUnits = weightUnitOptions.find(
      (option: IOption) => option.name?.toString() === DEFAULT_WEIGHT_MEASUREMENT
    )
    const defaultHeightUnits = heightUnitOptions.find(
      (option: IOption) => option.id?.toString() === DEFAULT_LENGTH_MEASUREMENT
    )
    const replacedPatientPCR: IPcr = {
      ...pcrDoc,
      ptid: null,
      patient: {
        ...pcrDoc.patient,
        patientDemographics: {
          ...initialPatientDemographicsState,
          physicianData: {
            ...physicianDetailsInit,
          },
          weightUnits: defaultWeightUnits || null,
          heightUnits: defaultHeightUnits || null,
        },
        patientAllergies: { ...initialPatientAllergiesState },
        patientMedications: { ...initialPatientMedicationsState },
        patientMedicalHistory: {
          ...initialPatientMedicalHistoryState,
          medicalHistory: [],
          medicalHistoryIcdNemsisBasic: [],
        },
        patientImmunizations: { ...initialPatientImmunizationsState },
      },
      billing: {
        insuranceInformation: { ...initialInsuranceInformationState },
        relationshipsAndGuarantors: { ...initialRelationshipsAndGuarantorsState },
        pcsInformation: { ...initialPcsInformationState },
        authorizationInformation: { ...initialAuthorizationInformationState },
        cmsInformation: { ...initialCmsInformationState },
        paymentInformation: { ...initialPaymentInformationState },
      },
    }

    yield call([pcrDocsService, pcrDocsService.savePCR], replacedPatientPCR)
    yield put(setSearchImport(false))
    yield put(setHasPatient(false))
    yield put(clearPhysicianData())
    yield put(resetEmployerState())
    yield put(refreshPatientData())
    yield put(refreshBillingData())
    rules.errors = clearPatientSearchErrors(rules.errors)
  } catch (e) {
    yield put(throwPcrException(`Error in replacePatient(): ${e}`))
  }
}

export function* replacePatientSaga() {
  yield takeLatest([PatientInformationConstants.REPLACE_PATIENT], performWithMutexHOG, replacePatient)
}
